import type { PayloadAction } from "@reduxjs/toolkit";
import type { EditorRootState } from "@v1/store/store";
import type { EditorCanvas } from "replo-utils/lib/misc/canvas";

import { createSelector, createSlice } from "@reduxjs/toolkit";
import { selectDraftElement_warningThisWillRerenderOnEveryUpdate } from "@v1/reducers/core-reducer";
import { findComponentById } from "@v1/utils/component";

export type CandidateState = {
  candidateNode: HTMLElement | null;
  lastCandidateComponentId: string | null;
  componentIdToDrag: string | null;
  componentNodeToDrag: HTMLElement | null;
  candidateCanvas: EditorCanvas | null;
};

const initialState: CandidateState = {
  candidateNode: null,
  lastCandidateComponentId: null,
  componentIdToDrag: null,
  componentNodeToDrag: null,
  candidateCanvas: null,
};

export type SetCandidateNodePayload = {
  candidateNode: HTMLElement | null;
  lastCandidateComponentId?: string | null;
  componentIdToDrag?: string | null;
  componentNodeToDrag?: HTMLElement | null;
  candidateCanvas: EditorCanvas | null;
};

const candidateSlice = createSlice({
  name: "candidate",
  initialState,
  reducers: {
    setCandidateNode: (
      state,
      action: PayloadAction<SetCandidateNodePayload>,
    ) => {
      // Note (Noah, 2024-07-08): Typescript/RTK don't play nicely with assigning
      // HTMLElement nodes to the state (errors about HTMLElement not being assignable
      // to properties of WritableDraft). There's probably a better way to go about
      // this, but this works for now
      // @ts-expect-error
      state.candidateNode = action.payload.candidateNode;
      state.candidateCanvas = action.payload.candidateCanvas;
      if (action.payload.lastCandidateComponentId !== undefined) {
        state.lastCandidateComponentId =
          action.payload.lastCandidateComponentId;
      }

      if (action.payload.componentIdToDrag !== undefined) {
        state.componentIdToDrag = action.payload.componentIdToDrag;
      }
      if (action.payload.componentNodeToDrag !== undefined) {
        // Note (Noah, 2024-07-08): Typescript/RTK don't play nicely with assigning
        // HTMLElement nodes to the state (errors about HTMLElement not being assignable
        // to properties of WritableDraft). There's probably a better way to go about
        // this, but this works for now
        // @ts-expect-error
        state.componentNodeToDrag = action.payload.componentNodeToDrag;
      }
    },
  },
});

const { actions, reducer } = candidateSlice;

export const selectCandidateNode = (state: EditorRootState) => {
  return state.candidate.candidateNode;
};

export const selectLastCandidateComponentId = (state: EditorRootState) => {
  return state.candidate.lastCandidateComponentId;
};

export const selectComponentIdToDrag = (state: EditorRootState) => {
  return state.candidate.componentIdToDrag;
};

export const selectComponentNodeToDrag = (state: EditorRootState) => {
  return state.candidate.componentNodeToDrag;
};

export const selectCandidateCanvas = (state: EditorRootState) => {
  return state.candidate.candidateCanvas;
};

export const selectCandidateComponent = createSelector(
  selectCandidateNode,
  selectDraftElement_warningThisWillRerenderOnEveryUpdate,
  (candidateNode, draftElement) => {
    if (!draftElement || !candidateNode) {
      return null;
    }
    const candidateComponentId = candidateNode.dataset.rid;
    return findComponentById(draftElement, candidateComponentId ?? null);
  },
);

export const { setCandidateNode } = actions;
export default reducer;
